import React, { useEffect, useState, Fragment } from 'react';
import { Formik, Form } from 'formik';
import { Column, Row } from '@xingternal/xing-grid';
import { FormField, Select, Option } from '@xingternal/text-field';
import { Label } from '@xingternal/label';
import { Checkbox } from '@xingternal/checkbox';
import { useSelector } from 'react-redux';

import styles from './PageForm.css';
import { FormValues, PageFormProps } from './PageForm/types';
import { validationSchema } from './PageForm/validation-schema';
import { TransitionButton } from './TransitionButton';
import { FooterButtons } from './FooterButtons';
import { TextEditorWithLanguageSelector } from './TextEditorWithLanguageSelector';
import { DateFormField } from './DateFormField';
import { RootState } from '../../../../redux';
import { VersionStatus } from '../../../../models/version';
import { StatusBanner } from '@xingternal/banner';

export const PageForm = ({
  page,
  version,
  initialValues,
  onLanguageChanged,
  onSaveVersion,
  onPublishVersion,
  onRevertVersion,
  onCloneVersion,
  embeddableForm,
  readOnly = false,
}: PageFormProps): React.ReactElement => {
  const [initialEditorData, setInitialEditorData] = useState<string>(null);
  const [initialApiData, setInitialApiData] = useState<string>(
    initialValues.rawText
  );

  const { loading: submitting } = useSelector<RootState, RootState['savePage']>(
    state => state.savePage
  );

  useEffect(() => {
    setInitialApiData(initialValues.rawText);
    setInitialEditorData(null);
  }, [initialValues.locale, version.id, initialValues.rawText]);

  initialValues = {
    ...initialValues,
    rawText: initialEditorData || initialValues.rawText,
  };

  return (
    <Formik<FormValues>
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSaveVersion}
      enableReinitialize
    >
      {({
        errors,
        values,
        setFieldValue,
        handleChange,
        dirty,
        handleReset,
      }): React.ReactElement => {
        return (
          <>
            {version.status == VersionStatus.scheduled && (
              <StatusBanner
                className={styles.scheduledNotification}
                display="inline"
                variant="info"
              >
                This scheduled page will be live at midnight in the
                Europe/Berlin timezone
              </StatusBanner>
            )}
            <Form>
              <div className={styles.fieldsContainer}>
                <div className={styles.headlineContainer}>
                  {!embeddableForm && (
                    <FormField
                      id="slug"
                      autoComplete="off"
                      label="URL to access the page"
                      size="small"
                      disabled={true}
                      className={styles.headline}
                      value={values.slug}
                    />
                  )}
                  <FormField
                    id="name"
                    data-cy="page-name"
                    autoComplete="off"
                    label="Page Name"
                    small
                    disabled={readOnly || submitting || embeddableForm}
                    className={styles.headline}
                    value={values.name || ''}
                    onChange={(
                      e: React.ChangeEvent<HTMLInputElement>
                    ): void => {
                      setFieldValue('name', e.target.value);
                    }}
                  />
                  <DateFormField
                    value={values.liveDate}
                    name="liveDate"
                    label="Live Date"
                    data={{ cy: 'live-date' }}
                    onChange={(date): void => {
                      setFieldValue('liveDate', date);
                    }}
                    disabled={readOnly || submitting}
                    error={errors.liveDate}
                    size="small"
                  />

                  {!embeddableForm && (
                    <Select
                      id="groupName"
                      data-cy="group-name"
                      value={values.groupName}
                      size="medium"
                      name="groupName"
                      placeholder="Product Group"
                      onChange={handleChange}
                      disabled={readOnly || submitting}
                      className={styles.groupButton}
                    >
                      <Option value="B2C">B2C</Option>
                      <Option value="B2B">B2B</Option>
                      <Option value="Legal Notices">Legal Notices</Option>
                      <Option value="Other Legal Pages">
                        Other Legal Pages
                      </Option>
                    </Select>
                  )}
                </div>

                <div className={styles.buttonsContainer}>
                  {!embeddableForm && (
                    <Fragment>
                      <Label>
                        <Checkbox
                          noLabel
                          name="nestedCounterList"
                          id="nestedCounterList"
                          data-cy="nested-counter-list"
                          className={styles.checkbox}
                          onChange={(
                            e: React.ChangeEvent<HTMLInputElement>
                          ): void => {
                            setFieldValue(
                              'nestedCounterList',
                              e.currentTarget.checked
                            );
                          }}
                          on={values.nestedCounterList}
                          disabled={readOnly || submitting}
                        />
                        Use decimal list (ex. 1.1)
                      </Label>
                      <Label>
                        <Checkbox
                          noLabel
                          name="hideToc"
                          id="hideToc"
                          data-cy="hide-toc"
                          className={styles.checkbox}
                          onChange={(
                            e: React.ChangeEvent<HTMLInputElement>
                          ): void => {
                            setFieldValue('hideToc', e.currentTarget.checked);
                          }}
                          on={values.hideToc}
                          disabled={readOnly || submitting}
                        />
                        Hide Table of Contents
                      </Label>
                      {values.groupName == 'B2B' && (
                        <Label>
                          <Checkbox
                            noLabel
                            name="includeGeneric"
                            id="includeGeneric"
                            data-cy="include-generic"
                            className={styles.checkbox}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>
                            ): void => {
                              setFieldValue(
                                'hasEmbedded',
                                e.currentTarget.checked
                              );
                            }}
                            on={values.hasEmbedded}
                            disabled={readOnly || submitting}
                          />
                          Include generic T&C
                        </Label>
                      )}
                    </Fragment>
                  )}
                  <TransitionButton
                    submitting={submitting}
                    page={page}
                    version={version}
                    onPublish={onPublishVersion}
                    onRevert={onRevertVersion}
                    onCloneVersion={onCloneVersion}
                    dirtyForm={dirty}
                  />
                </div>
              </div>
              <Row className={styles.textEditor}>
                <Column size={12}>
                  <TextEditorWithLanguageSelector
                    version={version}
                    readOnly={readOnly}
                    submitting={submitting}
                    onLanguageChanged={onLanguageChanged}
                    setInitialData={setInitialEditorData}
                    dirtyForm={dirty}
                    locale={initialValues.locale}
                    initialValue={initialApiData}
                  />
                </Column>
              </Row>
              <FooterButtons
                version={version}
                readOnly={readOnly}
                submitting={submitting}
                locale={initialValues.locale}
                dirtyForm={dirty}
                onEditCancelled={handleReset}
              />
            </Form>
          </>
        );
      }}
    </Formik>
  );
};
